import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, useApi, fmtDate, errMsg, imgSrc } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, ErrorBox, Empty, PageHead, Field } from "../components/common";

export default function News() {
  const [sp, setSp] = useSearchParams();
  const kind = sp.get("kind") || "news";
  const { data, loading, error, reload } = useApi(`/news?kind=${kind}`);
  return (
    <div>
      <PageHead eyebrow="News" title={kind === "newsletter" ? "The Kiln Letter" : "News from the Guild"} intro={kind === "newsletter" ? "Our monthly newsletter archive." : "Updates, exhibitions and member stories."}>
        <div role="tablist" className="mt-6 inline-flex rounded-full border border-line bg-white p-1">
          {[["news", "News"], ["newsletter", "The Kiln Letter"]].map(([k, l]) => <button key={k} role="tab" aria-selected={kind === k} onClick={() => setSp({ kind: k })} className={`rounded-full px-5 py-2 text-sm font-semibold ${kind === k ? "bg-ink text-paper" : "text-ink"}`} data-testid={`news-tab-${k}`}>{l}</button>)}
        </div>
      </PageHead>
      <section className="kh-container">
        {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={reload} /> : !data.length ? <Empty title="Nothing published yet" /> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.map((n) => (
              <Link key={n.id} to={`/news/${n.slug}`} className="kh-card overflow-hidden" data-testid={`news-card-${n.slug}`}>
                <img src={imgSrc(n.cover)} alt="" className="h-48 w-full object-cover" />
                <div className="p-5">
                  <p className="text-xs text-[#5a5f69]">{n.issue ? `Issue ${n.issue} · ` : ""}{fmtDate(n.date)}</p>
                  <h2 className="mt-1 text-2xl leading-snug">{n.title}</h2>
                  <p className="mt-2 text-sm text-[#4a4f59]">{n.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function NewsPost() {
  const { slug } = useParams();
  const { data: n, loading, error } = useApi(`/news/${slug}`);
  if (loading) return <Spinner />;
  if (error) return <div className="kh-container py-16"><ErrorBox message={error} /></div>;
  return (
    <article className="kh-container max-w-3xl pt-10">
      <Link to={`/news?kind=${n.kind}`} className="text-sm underline decoration-ochre" data-testid="back-to-news">← Back</Link>
      <p className="mt-6 text-sm text-[#5a5f69]">{n.author} · {fmtDate(n.date)}</p>
      <h1 className="mt-2 text-4xl sm:text-5xl">{n.title}</h1>
      <img src={imgSrc(n.cover)} alt="" className="my-8 h-72 w-full rounded-[2rem] object-cover" />
      <div className="prose-kh text-lg" data-testid="news-body" dangerouslySetInnerHTML={{ __html: n.body_html }} />
    </article>
  );
}

export function Contact() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/contact", f); setDone(data.message); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHead eyebrow="Contact" title="Talk to the Guild team" intro="Questions about membership, partnerships or introductions? We read every message." />
      <section className="kh-container grid gap-10 lg:grid-cols-2">
        {done ? <div className="kh-card p-8" role="status" data-testid="contact-success"><h2 className="text-3xl">Message received</h2><p className="mt-3 text-[#4a4f59]">{done}</p></div> : (
          <form onSubmit={submit} className="kh-card space-y-4 p-6 sm:p-8" data-testid="contact-form">
            <Field label="Your name" id="c-name"><input id="c-name" required value={f.name} onChange={set("name")} className="kh-input" data-testid="contact-name-input" /></Field>
            <Field label="Email" id="c-email"><input id="c-email" type="email" required value={f.email} onChange={set("email")} className="kh-input" data-testid="contact-email-input" /></Field>
            <Field label="Subject" id="c-subject"><input id="c-subject" required minLength={2} value={f.subject} onChange={set("subject")} className="kh-input" data-testid="contact-subject-input" /></Field>
            <Field label="Message" id="c-msg"><textarea id="c-msg" required minLength={5} rows={6} value={f.message} onChange={set("message")} className="kh-input" data-testid="contact-message-input" /></Field>
            <button disabled={busy} className="btn-primary" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send message"}</button>
          </form>
        )}
        <img src="/img/p3.jpg" alt="A colourful display of glazed ceramics" className="hidden h-full max-h-[520px] w-full rounded-[2rem] object-cover lg:block" />
      </section>
    </div>
  );
}

const DEMO = [["admin@kilnhouse.demo", "Admin (staff)"], ["studio@kilnhouse.demo", "Studio & Gallery member"], ["artist@kilnhouse.demo", "Professional Artist member"], ["student@kilnhouse.demo", "Student member"], ["free@kilnhouse.demo", "Free Community member"]];

export function DemoAccess() {
  const { isAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!window.confirm("Restore the default data? This removes all accounts and changes made since the last reset.")) return;
    setBusy(true);
    try { await api.post("/demo/reset"); toast.success("Default data restored. Please log in again if needed."); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHead eyebrow="Team access" title="Account access" intro="All accounts below use the password Demo2026!" />
      <section className="kh-container max-w-3xl">
        <ul className="divide-y divide-line rounded-2xl border border-line bg-white" data-testid="demo-accounts-list">
          {DEMO.map(([e, r]) => (
            <li key={e} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div><p className="font-medium text-ink">{r}</p><p className="font-mono text-sm text-[#4a4f59]">{e}</p></div>
              <Link to={`/login?email=${encodeURIComponent(e)}`} className="btn-ghost btn-sm" data-testid={`demo-login-${e.split("@")[0]}`}>Log in as this user</Link>
            </li>
          ))}
        </ul>
        <div className="mt-6 rounded-2xl bg-sand p-5 text-sm">
          <p><strong>Test card:</strong> 4242 4242 4242 4242 · any future expiry · any CVC. Decline test: 4000 0000 0000 0002.</p>
          {isAdmin ? <button disabled={busy} onClick={reset} className="btn-danger mt-4" data-testid="reset-demo-btn">{busy ? "Restoring…" : "Restore default data"}</button> : <p className="mt-2 text-[#4a4f59]">Log in as admin to see the “Restore default data” button.</p>}
        </div>
      </section>
    </div>
  );
}
